import { TUser } from '~/features/user/models'
import { ECognitoGroups, EFeatureCode } from '~/models/auth'

export enum EOrgBillingMode {
  paid = 'paid',
  inactive = 'inactive',
  demo = 'demo',
  internal = 'internal',
}

export enum EPhoneOrchestrationType {
  legacy = 'legacy',
  standard = 'standard',
  realtime = 'realtime',
}

export enum ELiveTransferLiveTransferType {
  dtmf = 'dtmf',
  voice = 'voice',
  both = 'both',
}

export type TOrganization = {
  id: string
  name?: string | null
  billingMode?: EOrgBillingMode | null
  createdDate: string
  referralCode: string
  referredByCode: string
  paymentTier: ECognitoGroups
  stripeCustomerId: string
  adminOwned: boolean
  agentLimit: number
  userLimit: number
  outboundCallLimit: number
  OrgFeature: TOrgFeature[]
  ofuContactLimit: number
  partnerId: string | null
  orgType: string | null
  phoneOrchestrationType?: EPhoneOrchestrationType | null
  User?: TUser[]
  Partner?: {
    partnerName: string
  }
  ProductHistory?: Array<{
    id: string
    orgId: string
    trialDuration: number
    tierStartDate: string
  }>
  liveTransferType: ELiveTransferLiveTransferType
}

export enum EOrgFeatureStatus {
  active = 'active',
  trial = 'trial',
  cancelled = 'cancelled',
  inactive = 'inactive',
}

export enum EFeatureType {
  baseTier = 'baseTier',
  addon = 'addon',
}

export type TFeature = {
  id: string
  code: EFeatureCode
  friendlyName?: string
  featureType: EFeatureType
  description?: string
  baseTier: ECognitoGroups | null
  active: boolean
  externalId?: string | null
}

export type TOrgFeature = {
  id: string
  orgId: string
  Feature: TFeature
  status: EOrgFeatureStatus
}

export enum EOrgPlanBillingInterval {
  month = 'month',
  year = 'year',
}

export enum EOrgPlanStatus {
  active = 'active',
  cancelled = 'cancelled',
}

export type TOrgPlanPlan = {
  id: string
  displayName: string
  internalName: string
  description: string | null
  status: string
  basePricePerLocation: number | null
  includedCalls: number | null
  includedChats: number | null
  includedEmails: number | null
  includedTexts: number | null
  overageRatePerCall: number | null
  overageRatePerChat: number | null
  overageRatePerEmail: number | null
  overageRatePerText: number | null
  paymentTier: string | null
}

export type TOrgPlan = {
  id: string
  createdDate: string
  updatedDate: string
  orgId: string
  planId: string
  startDate: string
  endDate: string | null
  billingDay: number | null
  billingAnchorDate: string | null
  billingInterval: EOrgPlanBillingInterval
  status: EOrgPlanStatus
  totalMonthlyBaseCost: number | null
  isCurrent: boolean
  cancellationRequestDate: string | null
  basePricePerLocationOverride: number | null
  billingDateIsFinal: boolean
  includedCallsOverride: number | null
  includedChatsOverride: number | null
  includedEmailsOverride: number | null
  includedTextsOverride: number | null
  locationsPurchased: number | null
  Plan: TOrgPlanPlan
}

export type TGetCurrentOrgPlanResponse = {
  orgPlan: TOrgPlan | null
}

export type TOrgPlanOverrides = {
  basePricePerLocationOverride?: number | null
  includedCallsOverride?: number | null
  includedChatsOverride?: number | null
  includedEmailsOverride?: number | null
  includedTextsOverride?: number | null
}

export type TCreateOrgPlanPayload = TOrgPlanOverrides & {
  planId: string
  billingAnchorDate: string
  billingInterval: EOrgPlanBillingInterval
  billingDateIsFinal: boolean
  locationsPurchased: number
}

// Commercial change: closes the current plan and opens a new one.
export type TAmendOrgPlanPayload = TOrgPlanOverrides & {
  effectiveDate: string
  planId: string
  locationsPurchased: number
}

// Data-entry fix: edits the current plan row in place. At least one field.
export type TCorrectOrgPlanPayload = TOrgPlanOverrides & {
  planId?: string
  locationsPurchased?: number
  billingAnchorDate?: string
  billingDateIsFinal?: boolean
}
